/**
 * グローバルヘッダーのテーマ切り替えトグル。
 *
 * `docs/guidelines/frontend.md` の Major 規約（トグルはグローバルヘッダに置き、選択を
 * localStorage へ永続する）に従う。永続と FOUC 防止は共有 `useTheme` と `index.html` の
 * インラインスクリプトが担う。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business グローバルヘッダーのテーマ切り替えトグル。共有 useTheme で dark / light を切り替える
 * @graph-connects useTheme [calls] テーマの解決と永続
 */
import { useTheme } from '@cortex/web-design-system/hooks/useTheme';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * spec-console のテーマ設定を保存する localStorage キー。
 * `index.html` の FOUC 防止スクリプトが読むキーと一致させる。
 * @graph-connects none
 */
export const THEME_STORAGE_KEY = 'spec-console-theme';

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business dark / light を切り替えるヘッダーボタンを描画する
 * @graph-connects useTheme [calls] テーマの解決と永続
 */
export function ThemeToggle() {
  const { t } = useTranslation();
  const { resolved, setTheme } = useTheme(THEME_STORAGE_KEY);

  return (
    <button
      type="button"
      onClick={() => {
        setTheme(resolved === 'dark' ? 'light' : 'dark');
      }}
      className="rounded-md border border-[var(--color-border)] p-2 text-[var(--color-text-primary)]"
      aria-label={resolved === 'dark' ? t('app.themeToLight') : t('app.themeToDark')}
    >
      {resolved === 'dark' ? (
        <Sun className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Moon className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}
